/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuthInactivity } from '../hooks/useAuthInactivity';
import { attachGuestHistory } from '../services/trackingService';
import {
  clearBrowserStorage,
  clearStoredAuthUser,
  clearToken,
  getStoredAuthUser,
  getToken,
  isTokenExpired,
  saveStoredAuthUser,
  saveToken,
} from '../utils/auth';
import { fetchCurrentUser, loginWithCredentials, sendLogoutRequest } from './authApi';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const authSeqRef = useRef(0);

  useAuthInactivity();

  const resetAuthState = useCallback(() => {
    clearToken();
    clearStoredAuthUser();
    setCurrentUser(null);
  }, []);

  const hydrateAuthenticatedUser = useCallback(async (user) => {
    setCurrentUser(user);
    saveStoredAuthUser(user);
    attachGuestHistory();
    return user;
  }, []);

  const init = useCallback(
    async (signal = null) => {
      const authSeq = authSeqRef.current + 1;
      authSeqRef.current = authSeq;
      const isCurrentAuth = () => authSeqRef.current === authSeq && !signal?.aborted;
      let cachedUser = null;

      setLoading(true);

      try {
        const token = getToken();
        const expiredToken = token && isTokenExpired(token);
        cachedUser = getStoredAuthUser();

        if (!token || expiredToken) {
          if (expiredToken) {
            localStorage.setItem('last_active_ts', String(Date.now()));
          }
          if (isCurrentAuth()) {
            resetAuthState();
          }
          return;
        }

        if (cachedUser && isCurrentAuth()) {
          setCurrentUser(cachedUser);
        }

        const user = await fetchCurrentUser();
        if (!isCurrentAuth()) return;

        if (!user) {
          resetAuthState();
          return;
        }

        await hydrateAuthenticatedUser(user);
      } catch (error) {
        if (!isCurrentAuth()) return;
        if (!cachedUser) {
          setCurrentUser(null);
        }
        console.error('Failed to restore authenticated session:', error);
      } finally {
        if (isCurrentAuth()) setLoading(false);
      }
    },
    [hydrateAuthenticatedUser, resetAuthState]
  );

  useEffect(() => {
    const controller = new AbortController();
    init(controller.signal);
    return () => controller.abort();
  }, [init]);

  // Handle session expiry events dispatched by apiClient on 401/403 responses.
  useEffect(() => {
    const handler = (event) => {
      const failedToken = event?.detail?.token;
      if (failedToken && getToken() && getToken() !== failedToken) {
        return;
      }
      authSeqRef.current += 1;
      resetAuthState();
      setLoading(false);
    };
    window.addEventListener('yubu-session-expired', handler);
    return () => window.removeEventListener('yubu-session-expired', handler);
  }, [resetAuthState]);

  const login = useCallback(
    async (credentials) => {
      if (!credentials || (!credentials.email && !credentials.password)) {
        return null;
      }

      const authSeq = authSeqRef.current + 1;
      authSeqRef.current = authSeq;
      const isCurrentAuth = () => authSeqRef.current === authSeq;

      setLoading(true);
      try {
        const data = await loginWithCredentials(credentials);
        if (!isCurrentAuth()) return null;

        const token = data.access_token;
        if (!token) {
          throw new Error('No access credential returned');
        }

        const tokenValue = typeof token === 'string' ? token : JSON.stringify(token);
        saveToken(tokenValue);

        const user = await fetchCurrentUser();
        if (!isCurrentAuth()) return null;

        if (!user) {
          throw new Error('Unable to load user after login');
        }

        return await hydrateAuthenticatedUser(user);
      } finally {
        if (isCurrentAuth()) setLoading(false);
      }
    },
    [hydrateAuthenticatedUser]
  );

  const logout = useCallback(async () => {
    const token = getToken();
    authSeqRef.current += 1;
    resetAuthState();
    setLoading(false);

    try {
      await sendLogoutRequest(token);
    } catch (error) {
      throw new Error(error.message || 'Logout failed');
    }
  }, [resetAuthState]);

  const clearSessionData = useCallback(() => {
    authSeqRef.current += 1;
    clearBrowserStorage();
    setCurrentUser(null);
    setLoading(false);
  }, []);

  const currentRole = currentUser?.role || null;

  const value = useMemo(
    () => ({
      currentUser,
      currentRole,
      loading,
      login,
      logout,
      clearSessionData,
      refresh: init,
    }),
    [clearSessionData, currentRole, currentUser, init, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
